import React from 'react';
import ReactQuill from 'react-quill';
import Popover from 'material-ui/Popover';
import UserActions from 'actions/UserActions';
import debounce from 'lodash/debounce';
import UserOptions from './UserOptions';
import getTaggedText from './util/getTaggedText';
import findMatches from './util/findMatches';
import Quill from 'quill';
import {List} from 'immutable';
import 'react-quill/dist/quill.bubble.css';
import './UserTaggableInput.styl';
import {addPrimaryBinding} from 'utils/quill/addPrimaryBinding';

let Inline = Quill.import('blots/inline');

class AnchorBlot extends Inline {}
AnchorBlot.blotName = 'anchorPoint';
AnchorBlot.tagName = 'anchorPoint';
AnchorBlot.className = 'anchor-point';

Quill.register(AnchorBlot);

const modules = {
  toolbar: false,
};

export default class UserTaggableInput extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      users: List(),
      taggedUsers: props.taggedUsers || List(),
      searchTerm: null,
      taggingInitiated: false,
    };
    this.onChange = this.onChange.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.searchUsers = debounce(
      (searchTerm, success) => UserActions.fetch({searchTerm, forMentions: true}).then(success),
      200
    );
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.quill.editor.focus();
    }

    if (this.props.onEnter) {
      addPrimaryBinding(this.quill.getEditor(), {
        key: 13,
        shiftKey: false,
        handler: this.props.onEnter,
      });
    }
  }

  onChange(markup, delta, source, editor) {
    // a delete is needed to remove the partially typed name
    // when they have finished searching, just ignore it
    // it will be replaced with the full name immediately after
    const deleteOp = delta.ops.find(op => op.delete);
    if (deleteOp && source === 'api') {
      return;
    }
    const matches = this.state.taggedUsers
      .flatMap(user => findMatches(editor.getText(), user))
      .sortBy(match => match.index);
    const html = editor.getHTML();
    const text = editor.getText().trim();
    let {ampersandPosition} = this.state;

    // embolden the matches and unbold the spaces in between matches
    const emboldedMatches = () => {
      const editor = this.quill && this.quill.getEditor();

      // for some reason quill is null sometimes.. seems like it only
      // happens right before it unmounts (but before componentDidUnmount)
      if (editor) {
        editor.formatText(0, matches.size > 0 ? matches.get(0).index : text.length, 'bold', false);

        matches.forEach((match, i) => {
          const taggedText = getTaggedText(match.user);
          editor.formatText(match.index, taggedText.length, 'bold', true);
          const nextMatch = matches.get(i + 1);
          const boldUntil = match.index + taggedText.length;
          editor.formatText(
            boldUntil,
            nextMatch ? nextMatch.index - boldUntil : text.length,
            'bold',
            false
          );
        });
      }
    };

    // for some reason this.quill is null on the initial run..
    // figure out something better than a setTimeout
    setTimeout(emboldedMatches);

    const taggingInitiated = delta.ops.some(op => op.insert === '@');
    const retainOp = delta.ops.find(op => op.retain);
    const cursorPosition = retainOp ? retainOp.retain : 0;
    let searchTerm;

    if (taggingInitiated) {
      this.quill && this.quill.getEditor().formatText(cursorPosition, 35, 'anchorPoint', true);
      this.anchorRef = document.querySelector('anchorPoint');
      ampersandPosition = cursorPosition;
      this.setState({taggingInitiated, ampersandPosition});
    } else if (deleteOp && this.state.taggingInitiated) {
      const ampersandDeleted =
        ampersandPosition >= cursorPosition &&
        ampersandPosition <= cursorPosition + deleteOp.delete;
      if (ampersandDeleted) {
        this.setState({taggingInitiated: false});
      } else {
        searchTerm = text.slice(ampersandPosition + 1, cursorPosition);
      }
    }

    const insertOp = delta.ops.find(op => op.insert);
    if (this.state.taggingInitiated && insertOp) {
      searchTerm = text
        .slice(ampersandPosition + 1, cursorPosition + insertOp.insert.length)
        .trim();
    }

    if (searchTerm) {
      this.searchUsers(searchTerm, users => {
        this.setState({
          users,
          searchTerm,
        });
      });
    } else {
      this.setState({searchTerm});
    }

    if (deleteOp) {
      this.setState({
        taggedUsers: this.state.taggedUsers.filter(user =>
          matches.some(match => match.user === user)
        ),
      });
    }
    this.props.onChange(html, this.state.taggedUsers);
  }

  onSelect(user) {
    const editor = this.quill.getEditor();
    editor.formatText(0, editor.getLength(), 'anchorPoint', false);
    editor.format('anchorPoint', false);
    this.setState(
      {
        taggingInitiated: false,
        taggedUsers: this.state.taggedUsers.push(user),
        users: List(),
        searchTerm: null,
      },
      () => {
        const editor = this.quill.getEditor();
        const position = editor.getSelection(true);
        const {ampersandPosition} = this.state;
        editor.deleteText(ampersandPosition, position.index - ampersandPosition);
        const taggedText = getTaggedText(user);
        editor.insertText(ampersandPosition, taggedText);
        editor.setSelection(ampersandPosition + taggedText.length);
      }
    );
  }

  resetTaggingState = () => {
    this.setState({
      taggingInitiated: false,
      users: List(),
      searchTerm: null,
    });
  };

  render() {
    const {inputRef} = this.props;
    const {searchTerm} = this.state;

    return (
      <div className="user-taggable-input" onClick={() => this.quill.editor.focus()}>
        <ReactQuill
          placeholder={this.props.placeholder}
          modules={modules}
          theme="bubble"
          ref={r => {
            this.quill = r;
            inputRef && inputRef(r);
          }}
          defaultValue={this.props.value}
          onChange={this.onChange}
          readOnly={this.props.disabled}
        />
        {this.state.taggingInitiated && (
          <Popover
            canAutoPosition={true}
            open={true}
            useLayerForClickAway={false}
            onRequestClose={() => this.setState({taggingInitiated: false})}
            className={'user-tag-popover'}
            anchorEl={this.anchorRef}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            targetOrigin={{
              horizontal: 'left',
              vertical: 'top',
            }}>
            {searchTerm && searchTerm.length > 0 ? (
              <UserOptions
                users={this.state.users}
                onSelect={this.onSelect}
                searchTerm={searchTerm}
                onCancel={this.resetTaggingState}
              />
            ) : (
              <div className="user-options no-matches">Type a user name to tag them...</div>
            )}
          </Popover>
        )}
      </div>
    );
  }
}
